import { isFinalStage } from "DAIEditor/components/EditSpeaker/helpers";

const RED_HERRING_ENABLED = (process.env.REACT_APP_RED_HERRING_ENABLED === "true");
const RED_HERRING_COUNT = 1;

function generateId() {
  return "rh-" + Math.random().toString(36).substring(2, 10);
}

function pickRandom(arr, count) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

function textToWords(text, startTime, endTime) {
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const duration = endTime - startTime;
  const step = duration / tokens.length;

  return tokens.map((token, i) => ({
    text: i === 0 ? token : " " + token,
    start: parseInt(startTime + i * step),
    end: parseInt(startTime + (i + 1) * step),
    confidence: 1,
    is_removed: false,
  }));
}

function msToTimestamp(totalMs) {
  const totalSeconds = Math.floor(totalMs / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function getTimeBounds(transcripts, insertIndex) {
  const prevStart =
    insertIndex > 0 ? transcripts[insertIndex - 1]?.start ?? 0 : 0;
  const nextStart =
    insertIndex < transcripts.length
      ? transcripts[insertIndex]?.start ?? prevStart + 30
      : prevStart + 30;
  return { prevStart, nextStart };
}

function extractSpeakers(transcripts) {
  const speakers = [
    ...new Set(transcripts.map((t) => t.speaker).filter(Boolean)),
  ];
  return speakers.length > 0 ? speakers : ["Speaker 1"];
}

const ALLOWED_UPLOAD_TYPES = new Set(["HUMAN", "FIRST_DRAFT"]);

export function injectRandomSnippets(transcripts, { uploadType, workflowStatus } = {}) {
  if (!RED_HERRING_ENABLED || !Array.isArray(transcripts) || transcripts.length < 2) {
    return transcripts;
  }

  if (!ALLOWED_UPLOAD_TYPES.has(uploadType) || isFinalStage(workflowStatus, uploadType)) {
    return transcripts;
  }

  const count = Math.min(RED_HERRING_COUNT, Math.max(1, Math.floor(transcripts.length / 2)));
  const chosen = pickRandom(randomSnippets, count);
  const speakers = extractSpeakers(transcripts);
  const result = [...transcripts];

  const halfwayIndex = Math.floor(result.length / 2);
  const insertionIndices = [];
  for (let i = 0; i < chosen.length; i++) {
    const idx =
      halfwayIndex + Math.floor(Math.random() * (result.length - halfwayIndex + 1));
    insertionIndices.push(idx);
  }
  insertionIndices.sort((a, b) => b - a);

  for (let i = 0; i < chosen.length; i++) {
    const insertIdx = insertionIndices[i] + i;
    const snippetId = generateId();
    const speaker = speakers[Math.floor(Math.random() * speakers.length)];
    const { prevStart, nextStart } = getTimeBounds(result, insertIdx);
    const midStart = prevStart + (nextStart - prevStart) * 0.25;
    const midEnd = prevStart + (nextStart - prevStart) * 0.75;
    const words = textToWords(chosen[i].text, midStart, midEnd);

    const snippet = {
      type: "snippet",
      id: snippetId,
      start_timestamp: msToTimestamp(midStart),
      start: parseInt(midStart),
      speaker,
      isRedHerring: true,
      children: [
        {
          type: "paragraph",
          snippetId,
          children: words.map((word, idx) => ({
            ...word,
            id: idx,
            snippetId,
            isRedHerring: true,
          })),
        },
      ],
    };

    result.splice(insertIdx, 0, snippet);
  }

  return result;
}

export function computeIntegrityCheck(editorChildren) {
  if (!RED_HERRING_ENABLED) {
    return null;
  }

  const hasRedHerrings = (editorChildren || []).some(
    (snippet) =>
      snippet.isRedHerring ||
      snippet.children?.[0]?.children?.some((w) => w.isRedHerring)
  );

  return !hasRedHerrings;
}

export function stripRedHerringWords(transcript) {
  if (!RED_HERRING_ENABLED || !Array.isArray(transcript)) {
    return transcript;
  }

  return transcript
    .map((snippet) => {
      const paragraph = snippet.children?.[0];
      const words = paragraph?.children;

      if (!Array.isArray(words)) {
        return snippet;
      }

      const hasAnyRedHerring = words.some((w) => w.isRedHerring);
      if (!hasAnyRedHerring) {
        return snippet;
      }

      const cleaned = words.filter((w) => !w.isRedHerring);
      if (cleaned.length === 0) {
        return null;
      }

      return {
        ...snippet,
        isRedHerring: undefined,
        children: [
          {
            ...paragraph,
            children: cleaned,
          },
        ],
      };
    })
    .filter(Boolean);
}

export function buildRedHerringNodes(children, { uploadType, workflowStatus } = {}) {
  if (!RED_HERRING_ENABLED || !Array.isArray(children) || children.length < 2) {
    return { insertions: [], ids: [] };
  }

  if (!ALLOWED_UPLOAD_TYPES.has(uploadType) || isFinalStage(workflowStatus, uploadType)) {
    return { insertions: [], ids: [] };
  }

  const count = Math.min(RED_HERRING_COUNT, Math.max(1, Math.floor(children.length / 2)));
  const chosen = pickRandom(randomSnippets, count);
  const speakers = extractSpeakers(children);

  const halfwayIndex = Math.floor(children.length / 2);
  const indices = [];
  for (let i = 0; i < chosen.length; i++) {
    indices.push(
      halfwayIndex + Math.floor(Math.random() * (children.length - halfwayIndex + 1))
    );
  }
  indices.sort((a, b) => b - a);

  const insertions = [];
  const ids = [];

  for (let i = 0; i < chosen.length; i++) {
    const at = indices[i];
    const snippetId = generateId();
    const speaker = speakers[Math.floor(Math.random() * speakers.length)];
    const { prevStart, nextStart } = getTimeBounds(children, at);
    const midStart = prevStart + (nextStart - prevStart) * 0.25;
    const midEnd = prevStart + (nextStart - prevStart) * 0.75;
    const words = textToWords(chosen[i].text, midStart, midEnd);

    const node = {
      type: "snippet",
      id: snippetId,
      start_timestamp: msToTimestamp(midStart),
      start: parseInt(midStart),
      speaker,
      isRedHerring: true,
      children: [
        {
          type: "paragraph",
          snippetId,
          children: words.map((word, idx) => ({
            ...word,
            id: idx,
            snippetId,
            isRedHerring: true,
          })),
        },
      ],
    };

    insertions.push({ node, at });
    ids.push(snippetId);
  }

  return { insertions, ids };
}

function getRedHerringStorageKey(workflowId, workflowStatus) {
  return `rh_${workflowId}_${workflowStatus}`;
}

export function saveRedHerringState(workflowId, workflowStatus, entries) {
  try {
    const key = getRedHerringStorageKey(workflowId, workflowStatus);
    localStorage.setItem(key, JSON.stringify(entries));
  } catch (e) {
    console.warn("Failed to save red herring state:", e);
  }
}

export function loadRedHerringState(workflowId, workflowStatus) {
  try {
    const key = getRedHerringStorageKey(workflowId, workflowStatus);
    const stored = localStorage.getItem(key);
    if (stored === null) return null;
    return JSON.parse(stored);
  } catch (e) {
    console.warn("Failed to load red herring state:", e);
    return null;
  }
}

export function clearRedHerringState(workflowId, workflowStatus) {
  try {
    const key = getRedHerringStorageKey(workflowId, workflowStatus);
    localStorage.removeItem(key);
  } catch (e) {
    console.warn("Failed to clear red herring state:", e);
  }
}

export function snapshotRedHerringState(editorChildren) {
  if (!RED_HERRING_ENABLED || !Array.isArray(editorChildren)) {
    return [];
  }

  const entries = [];
  let fullRedHerringsBefore = 0;

  editorChildren.forEach((snippet, index) => {
    const words = snippet.children?.[0]?.children;
    if (!Array.isArray(words)) return;

    const redHerringWords = [];
    const totalWords = words.length;
    let redHerringCount = 0;

    words.forEach((w, wordIndex) => {
      if (w.isRedHerring) {
        redHerringCount++;
        redHerringWords.push({
          wordIndex,
          word: {
            text: w.text,
            start: w.start,
            end: w.end,
            confidence: w.confidence,
            is_removed: w.is_removed,
          },
        });
      }
    });

    if (redHerringCount === 0) return;

    if (redHerringCount === totalWords) {
      entries.push({
        type: "full",
        insertIndex: index - fullRedHerringsBefore,
        snippetId: snippet.id,
        speaker: snippet.speaker,
        words: redHerringWords.map((rw) => rw.word),
      });
      fullRedHerringsBefore++;
    } else {
      entries.push({
        type: "partial",
        snippetId: snippet.id,
        wordEntries: redHerringWords,
      });
    }
  });

  return entries;
}

export function restoreRedHerringState(transcripts, savedEntries, { uploadType, workflowStatus } = {}) {
  if (!RED_HERRING_ENABLED || !Array.isArray(savedEntries) || savedEntries.length === 0) {
    return transcripts;
  }

  if (isFinalStage(workflowStatus, uploadType)) {
    return transcripts;
  }

  const result = [...transcripts];

  const fullEntries = savedEntries
    .filter((e) => e.type === "full")
    .sort((a, b) => a.insertIndex - b.insertIndex);

  const partialEntries = savedEntries.filter((e) => e.type === "partial");

  for (const entry of partialEntries) {
    const snippetIndex = result.findIndex((s) => s.id === entry.snippetId);
    if (snippetIndex === -1) continue;

    const snippet = result[snippetIndex];
    const paragraph = snippet.children?.[0];
    if (!paragraph) continue;

    const wordsCopy = [...paragraph.children];

    const sorted = [...entry.wordEntries].sort(
      (a, b) => b.wordIndex - a.wordIndex
    );

    for (const { wordIndex, word } of sorted) {
      const safeIdx = Math.min(wordIndex, wordsCopy.length);
      wordsCopy.splice(safeIdx, 0, {
        ...word,
        id: safeIdx,
        snippetId: entry.snippetId,
        isRedHerring: true,
      });
    }

    wordsCopy.forEach((w, i) => { w.id = i; });

    result[snippetIndex] = {
      ...snippet,
      children: [
        {
          ...paragraph,
          children: wordsCopy,
        },
      ],
    };
  }

  fullEntries.forEach((entry, i) => {
    const safeIdx = Math.min(entry.insertIndex + i, result.length);
    const snippetId = entry.snippetId || generateId();

    const snippet = {
      type: "snippet",
      id: snippetId,
      start_timestamp: "",
      start: entry.words[0]?.start ?? 0,
      speaker: entry.speaker,
      isRedHerring: true,
      children: [
        {
          type: "paragraph",
          snippetId,
          children: entry.words.map((word, idx) => ({
            ...word,
            id: idx,
            snippetId,
            isRedHerring: true,
          })),
        },
      ],
    };

    const { prevStart } = getTimeBounds(result, safeIdx);
    const midStart = snippet.start || prevStart;
    snippet.start_timestamp = msToTimestamp(midStart);

    result.splice(safeIdx, 0, snippet);
  });

  return result;
}

const randomSnippets = [
  {
    text: "Revenue for the quarter came in at $3.2 billion, up 12% year-over-year on a constant currency basis. Our gross margin expanded by 150 basis points to 64.3%, driven primarily by favorable product mix and operational efficiencies. Operating expenses as a percentage of revenue declined to 41.2% from 43.8% in the prior year period. We generated $892 million in free cash flow during the quarter, and we returned approximately $600 million to shareholders through a combination of dividends and share repurchases. For the full year, we now expect revenue growth of 10% to 11%, up from our prior guidance of 8% to 10%."
  },
  {
    text: "Looking at our segment performance, the North America business delivered comparable store sales growth of 6.2%, with traffic up 3.1% and average check increasing 3.1%. International segment comparable store sales grew 4.8%, led by strength in the UK and Australia. We opened 42 net new locations during the quarter, bringing our total system-wide restaurant count to 2,847. Our digital sales mix reached 38% of total revenue, up from 29% a year ago. We remain on track to achieve our long-term target of 45% digital mix by the end of fiscal 2028."
  },
  {
    text: "I want to highlight our balance sheet strength. We ended the quarter with $1.4 billion in cash and short-term investments, total debt of $3.8 billion, and a net leverage ratio of 1.9 times, well within our target range of 1.5 to 2.5 times. During the quarter, we successfully refinanced $750 million of senior notes at a weighted average coupon of 4.65%, which was 85 basis points below the maturity we replaced. Our weighted average cost of debt now stands at 4.2%, with a weighted average maturity of 6.3 years."
  },
  {
    text: "Turning to our pipeline, we have approximately $4.2 billion of identified opportunities across our target verticals. Deal flow has accelerated meaningfully over the past 90 days as borrowers seek refinancing solutions ahead of upcoming maturities. We closed $1.1 billion in new commitments during the quarter at a weighted average spread of SOFR plus 525 basis points with an average loan-to-value of 62%. The credit quality of the portfolio remains excellent, with only 1.2% of loans on our watch list and no new non-accruals during the period."
  },
  {
    text: "Our SaaS business continues to scale nicely. Annual recurring revenue reached $485 million, up 28% year-over-year. Net revenue retention was 118%, driven by strong expansion within our existing customer base. We added 342 new enterprise logos in the quarter, up from 287 in the year-ago period. The average contract value for new enterprise deals increased 15% to $127,000 annually. Customer acquisition costs came down 22% as our brand awareness and inbound demand generation efforts continue to gain traction."
  },
  {
    text: "From a regulatory perspective, we are pleased with the progress on our pending application. We have received positive feedback from the reviewing authorities and anticipate a decision by the end of Q3. In the meantime, we have been working closely with the integration planning team to ensure a smooth transition. We expect to realize approximately $180 million in annual run-rate cost synergies within 18 months of close, with an additional $60 million in revenue synergies phasing in over the following 12 months."
  },
  {
    text: "Commodity prices remained a headwind during the quarter. Beef costs were up approximately 11% year-over-year, chicken was up 6%, and dairy was up approximately 8%. Despite these pressures, we managed to expand restaurant-level margins by 40 basis points to 22.1% through a combination of menu pricing, waste reduction initiatives, and labor productivity improvements. We implemented a 2.5% menu price increase in January and expect to take an additional 1.5% to 2% increase in late April. Our current pricing remains below overall food-away-from-home inflation."
  },
  {
    text: "Let me provide an update on our capital allocation priorities. Year to date, we have deployed approximately $2.3 billion, including $1.5 billion in acquisitions, $450 million in organic capital expenditures, and $350 million in share repurchases. We have approximately $1.8 billion remaining on our current share repurchase authorization. Our board recently approved a 10% increase to the quarterly dividend, bringing the annualized dividend to $3.52 per share. We continue to target a total payout ratio of 60% to 70% of adjusted earnings."
  },
  {
    text: "The integration of the MedTech acquisition is proceeding ahead of schedule. We have already consolidated three manufacturing facilities and expect to complete the remaining two by the end of Q2. We are tracking to $45 million in annualized cost savings, which is $10 million above our initial estimate. Customer retention has been excellent at 97%, and we have already identified several cross-selling opportunities that we expect to contribute an incremental $20 million to $25 million in revenue over the next 12 months."
  },
  {
    text: "Our backlog stands at a record $14.7 billion, up 18% from a year ago. Book-to-bill ratio for the quarter was 1.3 times, the fourth consecutive quarter above 1.0. We are seeing particularly strong demand in our defense and aerospace segments, where backlog grew 24% year-over-year. Importantly, approximately 72% of our backlog is expected to convert to revenue within the next 24 months, providing good visibility into our near-term growth trajectory. We remain confident in our ability to deliver mid-to-high single-digit organic revenue growth over the medium term."
  },
  {
    text: "Adjusted EBITDA for the quarter was $312 million, representing a margin of 28.4%, compared to $278 million or 27.1% in the prior year period. The 130 basis point improvement was driven by operating leverage on higher volumes, favorable mix shift toward higher-margin recurring services, and the benefit of our ongoing cost optimization program. We expect full-year adjusted EBITDA in the range of $1.22 billion to $1.26 billion, implying a margin of approximately 28% at the midpoint."
  },
  {
    text: "In our wealth management division, assets under management reached $892 billion, up 14% year-over-year, benefiting from both market appreciation and $18 billion in net new assets. Fee-based advisory assets now represent 52% of total client assets, up from 47% a year ago. Our financial advisor headcount grew by 3% net to 14,200, with a focus on recruiting experienced advisors with established books of business. Average revenue per advisor increased 8% to $1.1 million on a trailing 12-month basis."
  },
  {
    text: "Production for the quarter totaled 245,000 barrels of oil equivalent per day, up 8% from the prior year. We achieved this while reducing our per-unit cash operating costs by 6% to $12.40 per BOE. Our realized oil price was $74.30 per barrel, and realized natural gas price was $2.85 per MCF. Capital expenditures for the quarter were $380 million, focused primarily on our Permian Basin and Eagle Ford operations. We drilled 47 gross wells and brought 52 wells online during the period."
  },
  {
    text: "Turning to our insurance segment, combined ratio improved to 94.2% from 96.8% in the year-ago quarter. Net written premiums grew 9% to $1.8 billion, with rate increases averaging 6.5% across our commercial lines portfolio. The loss ratio improved 180 basis points to 62.4%, reflecting favorable frequency trends and the benefit of our underwriting discipline. Catastrophe losses were $42 million in the quarter, or 2.3 points on the combined ratio, compared to $67 million or 4.0 points in the prior year."
  },
  {
    text: "Now I'd like to discuss our outlook for the second half. We are raising our full-year EPS guidance to a range of $7.25 to $7.45, up from $6.90 to $7.20 previously. This reflects the strong first half performance, the accretive impact of recent acquisitions, and a more favorable tax rate outlook. We now expect an effective tax rate of approximately 21% for the full year, down from our prior estimate of 22.5%, primarily due to the recognition of certain R&D tax credits."
  },
  {
    text: "Our cloud infrastructure business grew 34% year-over-year to $2.1 billion in quarterly revenue. We now have over 4,200 enterprise customers spending more than $1 million annually with us, up 28% from a year ago. Remaining performance obligations totaled $18.6 billion, providing substantial visibility into future revenue. Gross margins in the cloud segment expanded 200 basis points to 67%, as we continue to benefit from improved utilization rates and the shift toward higher-margin managed services."
  },
  {
    text: "We're seeing really strong momentum in our biologics pipeline. Our lead candidate, INX-4720, has now completed enrollment in the Phase 3 pivotal trial with 1,200 patients across 180 sites globally. We expect top-line data in the second half of calendar year 2027. Meanwhile, INX-3185, our next-generation oral therapy, reported positive Phase 2 results with a statistically significant improvement in the primary endpoint. We plan to initiate Phase 3 in Q1 of next year, subject to regulatory feedback from our upcoming Type B meeting with the FDA."
  },
  {
    text: "Occupancy in our same-store portfolio improved 120 basis points sequentially to 93.8%. Blended lease spreads were positive 4.2%, with new lease spreads at 12.6% and renewal spreads at 2.8%. We signed 1.4 million square feet of new and renewal leases during the quarter, with a weighted average lease term of 7.2 years. Tenant retention was 78%, above our historical average. Net effective rents on new leases reached $42 per square foot, a 6% increase year-over-year. Our same-store NOI grew 5.1% on a cash basis."
  },
  {
    text: "The supply chain disruptions we experienced earlier in the year have largely been resolved. Our inventory levels are now normalized at $1.2 billion, down from a peak of $1.5 billion in Q1. Inventory turns improved to 5.8 times from 4.9 times sequentially. We expect to see continued improvement in working capital efficiency over the coming quarters. Gross margin benefited from the easing of inbound freight costs, which declined approximately 15% from peak levels. We now expect gross margin for the full year in the range of 48% to 49%."
  },
  {
    text: "I want to spend a moment on our ESG initiatives. During the quarter, we achieved our interim target of reducing Scope 1 and 2 emissions by 35% versus our 2019 baseline. We invested $120 million in renewable energy projects, including a 50-megawatt solar installation at our largest manufacturing campus. Our workforce diversity metrics continue to improve, with women now representing 38% of management roles, up from 32% two years ago. We published our third annual sustainability report, which was independently verified for the first time."
  },
  {
    text: "Moving to our merchant acquiring business, total payment volume processed was $187 billion, up 16% year-over-year. We added approximately 45,000 net new merchant locations during the quarter. The take rate was stable at 22 basis points. Our value-added services revenue, which includes fraud prevention, data analytics, and working capital solutions, grew 31% and now represents 24% of total segment revenue. We believe this mix shift will continue to be a meaningful driver of margin expansion over the medium term."
  },
  {
    text: "In terms of geographic diversification, international markets now contribute 43% of consolidated revenue, up from 37% three years ago. During the quarter, we saw particularly strong performance in our Asia-Pacific region, where revenue grew 19% on a constant currency basis. EMEA was up 8% constant currency, while Latin America grew 14%. We have expanded our direct presence to 62 countries and continue to invest in local go-to-market capabilities, particularly in Southeast Asia and the Middle East."
  },
  {
    text: "Net interest income for the quarter was $4.8 billion, up 6% sequentially, driven by a 12 basis point expansion in net interest margin to 3.18%. Average loan balances grew 4% annualized, with particular strength in commercial real estate and middle market lending. Deposit costs increased 8 basis points sequentially but remain well-controlled relative to the rate environment. Our deposit beta on this tightening cycle has been approximately 42%, consistent with our modeling assumptions."
  },
  {
    text: "Our freight brokerage volume was up 3% year-over-year, which we view as encouraging given the continued soft truckload market. Revenue per load declined 2% as excess capacity continues to weigh on pricing. However, gross margin per load improved to $185, up 8% year-over-year, reflecting our investments in technology and carrier procurement capabilities. We are well-positioned to benefit from an eventual freight market recovery, with our carrier network having expanded to over 92,000 contracted carriers."
  },
  {
    text: "Subscription revenue grew 22% to $645 million, accounting for 71% of total revenue, up from 65% a year ago. This shift in mix continues to improve the predictability and quality of our revenue stream. Professional services revenue declined 8% as expected, as more customers adopt our self-service implementation tools. Total RPO was $3.2 billion, up 19%, with current RPO of $1.7 billion, up 21%. We expect subscription revenue growth of 20% to 22% for the full fiscal year."
  },
  {
    text: "Our direct-to-consumer channel continues to outperform. E-commerce revenue grew 27% year-over-year and now represents 34% of total brand revenue, up from 28% in the prior year. Average order value increased 5% to $94, and conversion rates improved by 40 basis points. We opened 18 net new retail stores during the quarter, bringing our total to 312 company-owned locations. Comparable store sales in our retail channel were up 8%, driven by strong traffic trends and effective visual merchandising."
  },
  {
    text: "Moving to our credit metrics, net charge-offs were 52 basis points, up from 43 basis points in the prior quarter but in line with our expectations as we normalize from historically low levels. The allowance for credit losses stands at 1.85% of total loans, reflecting an increase of $120 million during the quarter, primarily driven by a more cautious macroeconomic outlook. Our CET1 ratio improved to 11.8%, providing a substantial buffer above the regulatory minimum of 9.5% including all applicable buffers."
  },
  {
    text: "The performance of our data center portfolio has been exceptional. Power capacity under contract increased 42% year-over-year to 1.8 gigawatts. We signed 180 megawatts of new leases during the quarter at an average rate of $145 per kilowatt per month, reflecting strong demand from hyperscale and enterprise customers. Construction progresses on schedule for our three new campuses totaling 500 megawatts, with initial delivery expected in Q3 of next year. Pre-leasing on these projects is approximately 65%."
  },
  {
    text: "I'd also like to mention the progress we've made on our digital transformation. We have now migrated 78% of our workloads to the cloud, up from 55% at the start of the fiscal year. This has contributed to a 22% reduction in IT infrastructure costs. Our new ERP implementation went live in three additional markets during the quarter, and we expect to complete the global rollout by mid-2027. Early results are encouraging, with a 15% improvement in order-to-cash cycle times in the markets that have transitioned."
  },
  {
    text: "Gross written premiums in our reinsurance segment totaled $2.4 billion, up 11% year-over-year. The January 1st renewal season was favorable, with risk-adjusted rate increases averaging 8% across our property catastrophe book. We maintained our disciplined approach to underwriting, walking away from approximately $300 million in opportunities that did not meet our return hurdles. Our retrocession program is in place with the attachment point at $450 million, providing significant protection for the portfolio."
  },
  {
    text: "Now let me walk through the drivers of our margin expansion. Labor productivity improved 4% year-over-year as our investments in automation and training begin to yield results. Raw material costs as a percentage of revenue declined 90 basis points, reflecting favorable commodity hedging and improved procurement practices. SG&A expenses declined 60 basis points as a percentage of revenue, primarily driven by the consolidation of back-office functions. These improvements more than offset the 30 basis point headwind from higher freight costs."
  },
  {
    text: "Our mortgage origination volume was $8.2 billion in the quarter, down 12% sequentially due to seasonal factors and higher rates. However, purchase volumes were up 5% year-over-year, partially offsetting the decline in refinance activity. Gain on sale margins compressed 15 basis points to 285 basis points, reflecting competitive pricing dynamics. Our servicing portfolio stands at $218 billion in unpaid principal balance. The MSR asset was valued at 1.42% of UPB, up from 1.38% at the end of the prior quarter."
  },
  {
    text: "Turning to R&D, we invested $412 million in the quarter, or 14.8% of revenue, compared to $365 million or 14.2% a year ago. This increase is primarily related to our next-generation platform development, which we expect to launch in the second half of fiscal 2027. We filed 87 new patent applications during the quarter, bringing our total patent portfolio to over 3,200 active patents. We believe our continued investment in innovation is critical to maintaining our competitive moat and supporting long-term growth."
  },
  {
    text: "In our managed care segment, medical membership grew 4.2% year-over-year to 28.3 million members. The medical benefit ratio was 85.7%, up 40 basis points from the prior year, primarily due to higher utilization trends in outpatient procedures and pharmacy. Operating costs as a percentage of premiums improved by 30 basis points to 8.2%, driven by investments in digital health tools and automation. We reaffirm our full-year MBR guidance of 85.5% to 86.5% and expect to operate at the lower end of that range."
  },
  {
    text: "Let me also address the competitive landscape, because I know investors have been asking about this. We have not seen meaningful share losses in any of our core categories. In fact, our Nielsen tracked channel data shows we gained 80 basis points of market share over the past 12 months in our largest category. Our brand health metrics are strong, with unaided awareness at 72%, up 5 points year-over-year. Consumer purchase intent scores are at their highest level in three years. We believe our product innovation pipeline and marketing investments are resonating with consumers."
  },
  {
    text: "Funds from operations came in at $1.82 per unit, up 7% year-over-year. Same-property NOI growth was 4.3%, driven by a combination of rate increases and occupancy improvement. We completed $450 million of acquisitions during the quarter at a weighted average cap rate of 5.8%, including a Class A industrial portfolio in the Southeast. Dispositions totaled $120 million at a 5.2% cap rate, consistent with our portfolio recycling strategy. We raised our full-year FFO guidance by $0.05 to a range of $7.15 to $7.25 per unit."
  },
  {
    text: "Our advisory and transaction fees totaled $1.6 billion, up 24% year-over-year, reflecting a rebound in M&A activity. Announced deal volumes in our coverage universe increased approximately 30% compared to the prior year period. Equity underwriting fees were $340 million, up 45%, driven by an improvement in IPO issuance. Debt underwriting revenue grew 18% to $520 million, supported by strong leveraged finance activity. Our advisory pipeline remains robust, and we expect continued momentum through the remainder of the year."
  },
  {
    text: "Same-store sales increased 5.3% for the quarter, comprised of a 3.8% increase in average check and a 1.5% increase in transactions. Off-premise sales represented 22% of total revenue and grew 11% year-over-year, driven by investments in our own digital ordering platform, which now accounts for 68% of off-premise orders, up from 51% a year ago. Third-party marketplace sales have stabilized, and we continue to negotiate more favorable commission structures. Restaurant-level margins came in at 20.4%, up 80 basis points year-over-year."
  },
  {
    text: "Our private equity business had a strong quarter. We realized $2.1 billion in proceeds from portfolio exits, generating a gross IRR of 2.4 times invested capital on those realizations. Fundraising momentum continued, with $3.8 billion raised across strategies during the quarter, including the final close of our flagship Fund IX at $12 billion, above our $10 billion target. Unrealized gains in the portfolio increased by $800 million, driven by strong operating performance across our portfolio companies."
  },
  {
    text: "Let me give you an update on our restructuring program. We are on track to deliver $200 million in annualized savings by the end of fiscal 2027. Through the first two quarters, we have realized approximately $85 million in savings and incurred $110 million in one-time charges. The program includes the closure of two manufacturing plants, the consolidation of seven regional offices, and a reduction of approximately 2,200 positions. We expect the remaining charges of approximately $60 million to be incurred over the next two quarters."
  },
  {
    text: "Vehicle deliveries totaled 128,000 units in the quarter, up 22% year-over-year. Average transaction prices remained strong at $52,400, up 3% from the prior year as customers continue to opt for higher-trim configurations. Our order bank stands at approximately 85,000 units, representing roughly four months of production at current rates. We launched our new mid-size SUV in March, and initial demand signals are very encouraging, with first-month orders exceeding our internal projections by approximately 40%."
  },
  {
    text: "Now, on the topic of tariffs and trade policy, we have been proactively managing our supply chain exposure. Approximately 15% of our cost of goods sold is sourced from countries subject to the current tariff actions. We have already begun shifting approximately $200 million in annual procurement to alternative suppliers in countries not affected by the current tariffs. In addition, we have implemented selective price increases of 2% to 4% on affected product lines. We estimate the net impact of tariffs on our fiscal 2027 earnings will be approximately $0.15 to $0.20 per share."
  },
  {
    text: "Our asset management division reported net inflows of $12.4 billion during the quarter, the strongest quarter in three years. Fixed income strategies attracted $8.2 billion, while equity strategies saw inflows of $3.1 billion. Alternative investment strategies contributed the remaining $1.1 billion. Total assets under management reached $1.24 trillion, up 11% year-over-year. Investment performance remains solid, with 72% of our fund AUM outperforming their respective benchmarks over the trailing three-year period."
  },
  {
    text: "We deployed 14,200 new fiber-to-the-home passings in the quarter, bringing our total to 6.8 million passings. Penetration on mature cohorts continues to climb, now averaging 44%, up from 41% a year ago. Broadband ARPU grew 3.5% year-over-year to $68.40, driven by customers upgrading to higher speed tiers. Customer churn improved 20 basis points year-over-year to 1.3% per month. We now expect to deploy between 50,000 and 55,000 new fiber passings for the full year, slightly above our initial guidance."
  },
  {
    text: "Adjusted operating income in our industrial segment was $184 million, up 15% year-over-year, with margins expanding 170 basis points to 18.2%. The improvement was broad-based, with all five business units contributing to margin expansion. Orders grew 9% organically, with particular strength in water infrastructure and smart metering solutions. We continue to see healthy demand from municipal customers, supported by federal infrastructure spending. The pipeline of identified municipal projects exceeds $2 billion over the next three to five years."
  },
  {
    text: "Now, I'd like to turn to the expert's perspective on the competitive dynamics in the semiconductor equipment space. What we're seeing is a fundamental shift in the capex cycle. The transition to gate-all-around transistor architectures at the leading edge is driving a significant increase in process complexity, and that translates directly to higher equipment intensity per wafer start. Our estimates suggest that a leading-edge fab being built today requires roughly 30% more lithography and deposition steps compared to the prior node. This is a structural tailwind for equipment suppliers."
  },
  {
    text: "Working capital was a source of cash of $95 million in the quarter. Accounts receivable DSO improved to 42 days from 47 days in the prior quarter, reflecting improved collections discipline and the resolution of several large disputed invoices. Inventory DIO increased modestly to 68 days from 65 days as we strategically built safety stock ahead of the expected seasonal demand peak. Accounts payable DPO was stable at 55 days. We expect working capital to be a modest use of cash in Q4 as we build inventory for the holiday season."
  },
  {
    text: "Comparable sales for the quarter increased 3.7%, reflecting strong execution across our portfolio of brands. Olive Garden delivered comparable sales growth of 4.2%, outperforming the industry by approximately 300 basis points. LongHorn Steakhouse grew comparable sales 5.8%, marking the 12th consecutive quarter of outperformance versus the casual dining benchmark. Our fine dining segment posted comparable sales of 2.1%, a solid result given the more challenging comparisons. Overall, we continue to believe our focus on culinary innovation and operational simplification is driving sustainable guest count growth."
  },
  {
    text: "From a credit quality standpoint, the portfolio continues to perform well. The weighted average risk rating improved to 2.8 from 2.9 in the prior quarter on our 1-to-5 scale. Non-accrual loans represented just 0.8% of the portfolio at fair value, down from 1.1% at the end of the prior quarter. We had one new non-accrual totaling $12 million during the quarter, which was more than offset by the resolution of two previously non-accruing investments. The weighted average yield on the portfolio was 12.4%, reflecting the attractive risk-return profile of our investments."
  },
  {
    text: "Before I wrap up, I want to highlight the momentum we're seeing in our loyalty program. Total membership surpassed 45 million, up 18% year-over-year. Loyalty members accounted for 62% of sales during the quarter, up from 54% a year ago. Importantly, the average spend per loyalty member is 2.3 times that of a non-member. Redemption rates remain healthy, suggesting strong engagement. We recently launched a co-branded credit card that is exceeding sign-up expectations, with 210,000 new cardholders in just the first two months since launch."
  },
  {
    text: "Our exploration program continues to deliver results. During the quarter, we announced a significant discovery at the Magellan prospect in the deepwater Gulf of Mexico, with an estimated recoverable resource of 150 to 200 million barrels of oil equivalent. This follows the successful appraisal of our Vega discovery, which has been upgraded to an estimated 300 million barrels recoverable. We expect to sanction the Vega development in the first half of 2027, with first production targeted for 2030. Our exploration success rate over the past 24 months stands at 62%, well above the industry average."
  },
];

// Helper functions to scan for partial fake paragraph matches in exported json files.
// Can be run in the browser console.

function normalizeText(text) {
  return text.toLowerCase().replace(/[^\w\s]/g, "").replace(/\s+/g, " ").trim();
}

function splitWords(text) {
  return normalizeText(text).split(" ").filter(Boolean);
}

function findLongestMatch(snippetWords, paragraphNorm, startIdx) {
  let end = startIdx + 5;
  while (end <= snippetWords.length) {
    const candidate = snippetWords.slice(startIdx, end).join(" ");
    if (!paragraphNorm.includes(candidate)) break;
    end++;
  }
  return snippetWords.slice(startIdx, end - 1).join(" ");
}

async function scanTranscriptForFakes(jsonFileName) {
  if (!jsonFileName) {
    console.error("Usage: scanTranscriptForFakes('filename.json')");
    return [];
  }

  const url = "/" + encodeURIComponent(jsonFileName);
  let data;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    data = await res.json();
  } catch (err) {
    console.error(
      `Failed to fetch "${jsonFileName}". Make sure the file is in the public/ folder.`,
      err
    );
    return [];
  }

  const transcript = data.transcript;
  if (!Array.isArray(transcript)) {
    console.error("JSON does not contain a valid 'transcript' array.");
    return [];
  }

  const results = [];

  for (let tIdx = 0; tIdx < transcript.length; tIdx++) {
    const para = transcript[tIdx];
    const paraText = para.text || "";
    const paraNorm = normalizeText(paraText);

    for (const snippet of randomSnippets) {
      const snippetText = snippet.text;
      const snippetNorm = normalizeText(snippetText);

      if (paraNorm.includes(snippetNorm)) {
        results.push({
          fake_snippet: snippetText,
          transcript_text: paraText,
          match: snippetText,
          transcript_index: tIdx + 1,
          start_timestamp: para.start_timestamp || "",
        });
        continue;
      }

      const snippetWords = splitWords(snippetText);
      if (snippetWords.length < 5) continue;

      let bestMatch = null;
      for (let i = 0; i <= snippetWords.length - 5; i++) {
        const window5 = snippetWords.slice(i, i + 5).join(" ");
        if (paraNorm.includes(window5)) {
          const longest = findLongestMatch(snippetWords, paraNorm, i);
          if (!bestMatch || longest.length > bestMatch.length) {
            bestMatch = longest;
          }
          i += longest.split(" ").length - 1;
        }
      }

      if (bestMatch) {
        results.push({
          fake_snippet: snippetText,
          transcript_text: paraText,
          match: bestMatch,
          transcript_index: tIdx + 1,
          start_timestamp: para.start_timestamp || "",
        });
      }
    }
  }

  if (results.length === 0) {
    console.log("No matches found.");
  } else {
    console.log(`Found ${results.length} match(es):`);
    console.table(
      results.map((r) => ({
        transcript_index: r.transcript_index,
        start_timestamp: r.start_timestamp,
        match: r.match.length > 80 ? r.match.slice(0, 77) + "..." : r.match,
        fake_snippet:
          r.fake_snippet.length > 60
            ? r.fake_snippet.slice(0, 57) + "..."
            : r.fake_snippet,
      }))
    );
  }

  return results;
}

window.scanTranscriptForFakes = scanTranscriptForFakes;

export default randomSnippets;
